import { Button, Stack, Typography } from '@mui/material';
import {
  useApp,
  useSelectedValueAttributeByKey,
  useSelectedValueByVariableKey,
  useTriggerAction,
} from '@k3/core';
import { isArray, partition } from 'lodash';
import { useIsKioskMode } from '../../hooks/useIsKioskMode';
import {
  LabelAndValues,
  useHeight,
  useLabelAndValue,
  useNumberLabelAndNumber,
} from '../../hooks/useHeight';
import { useDealerTheme } from '../../hooks/useDealerTheme';
import { useIsEcktisch } from '../../hooks/useShopName';
import { useMemo } from 'react';

export const Overview = () => {
  return (
    <Stack sx={{ p: 3 }}>
      <OverviewTable />
      <Stack sx={{ m: 2 }} />
      <FinishButtons />
    </Stack>
  );
};

const usps = [
  'Schreinerqualität - Made in Germany',
  'Individuell für Sie gefertigt',
  '5 Jahre Garantie',
];

const OverviewTable = () => {
  const { variableFontColor } = useDealerTheme();

  const edgeDekorLabel = useLabelAndValue('edgeDekor').variable;
  const plateMaterial = parseInt(
    (useSelectedValueAttributeByKey('plateMaterial', 'profiltyp')
      ?.value as string) ?? '0'
  );
  const edgeDekor =
    plateMaterial === 0
      ? useLabelAndValue('edgeDekor').value
      : `${useLabelAndValue('edgeDekor').value}<br/>${
          useLabelAndValue('edgeForm').value
        }, ${useLabelAndValue('cornerRadius').value}`;

  const model = useLabelAndValue('model');
  const dekor = useLabelAndValue('dekor');
  const height = useHeight();
  const depth = useNumberLabelAndNumber('depth');
  const width = useNumberLabelAndNumber('width');
  const sbl = useNumberLabelAndNumber('schenkelBreiteLinks');
  const sbr = useNumberLabelAndNumber('schenkelBreiteRechts');
  const stl = useNumberLabelAndNumber('schenkelTiefeLinks');
  const str = useNumberLabelAndNumber('schenkelTiefeRechts');
  const isEcktisch = useIsEcktisch();

  const data = useMemo(() => {
    const d: LabelAndValues[] = [];

    if (isEcktisch) {
      d.push({ variable: '', value: ['Linker Schenkel', 'Rechter Schenkel'] });
    }
    d.push(model);
    if (isEcktisch) {
      d.push(
        { ...depth, value: [stl.value, str.value] },
        { ...height, value: [height.value, height.value] },
        { ...width, value: [sbl.value, sbr.value] }
      );
    } else {
      d.push(depth, height, width);
    }

    let maxLoad = 100;
    if (model?.value === 'Flextisch') {
      maxLoad = 50;
    } else if (model?.value === 'Flex Ecktisch') {
      maxLoad = 75;
    }
    const load = { variable: 'Maximale Belastbarkeit', value: `${maxLoad} kg` };

    d.push({ variable: 'Plattenstärke', value: '2.5 cm' }, load, dekor, {
      variable: edgeDekorLabel,
      value: edgeDekor,
    });

    return d.map((e) => ({
      ...e,
      value: isArray(e.value) ? e.value : [e.value],
    }));
  }, [model, dekor, height, depth, width, sbl, sbr, stl, str, isEcktisch]);

  return (
    <Stack>
      <Stack mb={2}>
        {usps.map((usp) => (
          <Typography
            key={usp}
            sx={{ color: variableFontColor, lineHeight: '2rem' }}
          >
            <Typography
              component="span"
              sx={{ fontWeight: 'bold', fontFamily: 'montserrat' }}
            >
              {'\u2714 '}
            </Typography>
            <Typography component="span" sx={{ fontWeight: 'bold' }}>
              {' '}
              {usp}
            </Typography>
          </Typography>
        ))}
      </Stack>
      <Typography
        sx={{
          color: variableFontColor,
          lineHeight: '2rem',
          fontWeight: 'bold',
        }}
      >
        Alle Daten auf einen Blick:
      </Typography>
      <div>
        {data.map((d, i) => (
          <Stack
            key={d.variable}
            sx={{
              width: '100%',
              backgroundColor: i % 2 ? undefined : '#f6f6f6',
              p: 0.5,
              flexDirection: 'row',
            }}
          >
            <Typography
              component="span"
              sx={{
                width: isEcktisch ? '40%' : '60%',
                display: 'inline-block',
              }}
            >
              {d.variable}
            </Typography>
            {d.value.map((v, i) => (
              <Typography
                key={i}
                component="span"
                sx={{
                  width: `${50 / d.value.length}%`,
                  display: 'inline-block',
                }}
                dangerouslySetInnerHTML={{ __html: v }}
              />
            ))}
          </Stack>
        ))}
      </div>
    </Stack>
  );
};

const FinishButtons = () => {
  const order = useTriggerAction();
  const kiosk = useIsKioskMode();
  const actions = useApp().settings.actions.slice().reverse();
  const app = useApp();
  const [[mainAction], sideActions] = partition(actions, (a) =>
    kiosk ? a.pdf : a.id === app.settings.defaultAction
  );
  const pdfAction = sideActions.find((a) => a.pdf) ?? sideActions[0];
  const saveAction = sideActions.find((a) => !a.pdf) ?? sideActions[1];
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridRow: 'auto auto',
        gridColumnGap: '20px',
        gridRowGap: '20px',
      }}
    >
      {mainAction && (
        <Button
          variant="contained"
          onClick={() => order(mainAction)}
          sx={{ gridColumn: '1 / -1' }}
        >
          {mainAction.label.de}
        </Button>
      )}
      {!kiosk && (
        <>
          {pdfAction && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => order(pdfAction)}
              fullWidth
              sx={{ gridColumn: '1 / 1' }}
            >
              {pdfAction.label.de}
            </Button>
          )}

          {saveAction && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => order(saveAction)}
              fullWidth
              sx={{ gridColumn: '2 / 2' }}
            >
              {saveAction.label.de}
            </Button>
          )}
        </>
      )}
    </div>
  );
};
